import {Component, ElementRef, inject, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {Store} from "@ngrx/store";
import {combineLatest, filter, skip, Subject, Subscription, take} from "rxjs";
import {debounceTime, distinctUntilChanged} from "rxjs/operators";
import {FundValuationOverviewActions} from "../store/fund.actions";
import {
    selectFundValuationOverviewSearchContent,
    selectFundValuationOverviewSearchLoading,
    selectFundValuationOverviewSearchParams,
    selectFundValuationOverviewSearchTotalElements
} from "../store/fund-valuation/fund-valuation.selectors";
import {Router} from "@angular/router";
import {Sort} from "@angular/material/sort";
import {PageEvent} from "@angular/material/paginator";
import {MatDialog} from "@angular/material/dialog";
import {CodeTableEnum} from "../../shared/model/code";
import {selectSettingsLoaded, selectTableColumns, selectTablePageSize, selectTableSort} from "../../shared/user-setting/store/user-setting.selectors";
import {UserSettingActions} from "../../shared/user-setting/store/user-setting.actions";
import {TableSettingsDialogComponent, TableSettingsDialogData, TableSettingsDialogResult} from "../../shared/user-setting/table-settings-dialog/table-settings-dialog.component";
import {FundValuationOverviewItem} from "../models/fund-valuation-overview-item";
import {SelectionModel} from "@angular/cdk/collections";
import {ContextMenuComponent, ContextMenuItem} from "../../shared/components/context-menu/context-menu.component";
import {ConfirmationComponent} from "../../shared/components/confirmation/confirmation.component";
import {AuthenticationService} from "../../shared/services/login/authentication.service";
import {DELETE_ACCESS} from "../../shared/model/login/auth.constants";

const TABLE_KEY = "fund-valuation-overview";

const ALL_COLUMN_GROUPS = [
    {
        label: "Main",
        cols: [
            "fundValuationId",
            "description",
            "fundName",
            "valuationType",
            "reportDate",
            "closingDate",
            "country",
            "currencyIso",
            "fundNAV",
            "finalBidPrice",
            "discountToNAV",
            "deals"
        ]
    },
];

const COLUMN_LABELS: Record<string, string> = {
    fundValuationId: "ID",
    description: "Description",
    fundName: "Fund",
    valuationType: "Valuation Type",
    reportDate: "Report Date",
    closingDate: "Closing Date",
    country: "Country",
    currencyIso: "Currency",
    fundNAV: "NAV",
    finalBidPrice: "Final Bid",
    discountToNAV: "Discount to NAV",
    deals: "Deals",
};

const SORTABLE_COLUMNS = [
    {value: "id", label: "ID"},
    {value: "fundName", label: "Fund"},
    {value: "description", label: "Description"},
    {value: "reportDate", label: "Report Date"},
    {value: "closingDate", label: "Closing Date"},
    {value: "country", label: "Country"},
    {value: "currencyIso", label: "Currency"},
    {value: "fundNAV", label: "NAV"},
    {value: "finalBidPrice", label: "Final Bid"},
    {value: "discountToNAV", label: "Discount to NAV"},
];

@Component({
    selector: "valumize-fund-valuation-overview-page",
    templateUrl: "./fund-valuation-overview-page.component.html",
    styleUrls: ["./fund-valuation-overview-page.component.scss"],
    standalone: false,
    host: {class: "overview-page"}
})
export class FundValuationOverviewPageComponent implements OnInit, OnDestroy {
    private static readonly ALL_VALID_COLUMNS = ALL_COLUMN_GROUPS.flatMap(g => g.cols);

    private readonly store = inject(Store);
    private readonly router = inject(Router);
    private readonly dialog = inject(MatDialog);
    private readonly authService = inject(AuthenticationService);

    private readonly searchSubject = new Subject<string>();
    private searchSubscription?: Subscription;
    private readonly settingsSubscription?: Subscription;
    private columnsSubscription?: Subscription;
    private dialogSubscription?: Subscription;

    @ViewChild("searchInput") searchInput?: ElementRef<HTMLInputElement>;
    @ViewChild("ctxMenu") contextMenu?: ContextMenuComponent;

    readonly selection = new SelectionModel<FundValuationOverviewItem>(true, [], true, (a, b) => a.fundValuationId === b.fundValuationId);
    selectMode = false;

    columns = [
        "fundValuationId",
        "description",
        "fundName",
        "valuationType",
        "reportDate",
        "closingDate",
        "country",
        "currencyIso",
        "fundNAV",
        "finalBidPrice",
        "discountToNAV",
        "deals"
    ];
    codeTableCountry = CodeTableEnum.SHARED_COUNTRY;
    codeTableCurrency = CodeTableEnum.SHARED_CURRENCY;
    codeTableValuationType = CodeTableEnum.FUND_VALUATIONTYPE;
    searchVisible = false;

    entries$ = this.store.select(selectFundValuationOverviewSearchContent);
    totalElements$ = this.store.select(selectFundValuationOverviewSearchTotalElements);
    params$ = this.store.select(selectFundValuationOverviewSearchParams);
    isLoading$ = this.store.select(selectFundValuationOverviewSearchLoading);

    sortActive = "fundName";
    sortDirection: "asc" | "desc" | "" = "asc";

    constructor() {
        this.store.dispatch(FundValuationOverviewActions.resetSearch());

        this.settingsSubscription = this.store.select(selectSettingsLoaded).pipe(
            filter(Boolean),
            take(1)
        ).subscribe(() => {
            this.applySettingsFromStore();
        });
    }

    ngOnInit() {
        this.searchSubscription = this.searchSubject.pipe(
            debounceTime(300),
            distinctUntilChanged()
        ).subscribe(search => {
            // Selection is kept across pagination (WI 992), but a search change filters the row
            // set — clear the selection so a batch delete can't target now-invisible rows.
            this.selection.clear();
            this.store.dispatch(FundValuationOverviewActions.changeSearch({search}));
        });

        this.columnsSubscription = this.store.select(selectTableColumns(TABLE_KEY)).pipe(
            skip(1),
            filter((cols): cols is string[] => !!cols)
        ).subscribe(cols => {
            const valid = this.filterValidColumns(cols);
            if (valid) this.columns = valid;
        });
    }

    get displayedColumns(): string[] {
        return this.selectMode ? ["select", ...this.columns] : this.columns;
    }

    enableSelectMode(row: FundValuationOverviewItem) {
        this.selectMode = true;
        this.selection.select(row);
    }

    exitSelectMode() {
        this.selection.clear();
        this.selectMode = false;
    }

    onRowContextMenu(event: MouseEvent, row: FundValuationOverviewItem) {
        // While multi-selecting, the context menu manages the clicked row's membership in the batch
        // (the toolbar handles the batch-wide actions).
        if (this.selectMode) {
            const isSelected = this.selection.isSelected(row);
            const selectItems: ContextMenuItem[] = [
                {
                    label: isSelected ? "Deselect" : "Select",
                    icon: isSelected ? "check_box_outline_blank" : "check_box",
                    action: () => this.selection.toggle(row)
                },
                {
                    label: "Clear selection",
                    icon: "deselect",
                    action: () => this.exitSelectMode()
                }
            ];
            this.contextMenu?.open(event, selectItems);
            return;
        }
        // Right-clicking a row targets just that row.
        if (!this.selection.isSelected(row)) {
            this.selection.clear();
            this.selection.select(row);
        }
        const items: ContextMenuItem[] = [
            {
                label: "Select",
                icon: "check_box",
                action: () => this.enableSelectMode(row)
            },
            {
                label: "Delete",
                icon: "delete",
                hidden: !this.canDelete,
                action: () => this.deleteValuation(row)
            }
        ];
        this.contextMenu?.open(event, items);
    }

    get canDelete(): boolean {
        return this.authService.currentUser?.hasAnyPermission(DELETE_ACCESS) ?? false;
    }

    deleteValuation(row: FundValuationOverviewItem) {
        const dialogRef = this.dialog.open(ConfirmationComponent);
        dialogRef.componentInstance.confirmMessage = "Delete Fund Valuation";
        dialogRef.componentInstance.htmlContent =
            "<p>Are you sure you want to delete this fund valuation?</p><p><strong>This action cannot be undone.</strong></p>";
        dialogRef.afterClosed().pipe(take(1)).subscribe(confirmed => {
            if (confirmed) {
                this.store.dispatch(FundValuationOverviewActions.deleteValuation({
                    fundId: row.fundId,
                    fundReportId: row.fundReportId,
                    fundValuationId: row.fundValuationId
                }));
            }
        });
    }

    deleteSelected() {
        const valuations = this.selection.selected;
        if (valuations.length === 0) {
            return;
        }
        const dialogRef = this.dialog.open(ConfirmationComponent);
        dialogRef.componentInstance.confirmMessage = `Delete ${valuations.length} fund valuation${valuations.length > 1 ? "s" : ""}?`;
        dialogRef.componentInstance.htmlContent =
            `<p>Are you sure you want to delete ${valuations.length} fund valuation${valuations.length > 1 ? "s" : ""}?</p><p><strong>This action cannot be undone.</strong></p>`;
        dialogRef.afterClosed().pipe(take(1)).subscribe(confirmed => {
            if (confirmed) {
                this.store.dispatch(FundValuationOverviewActions.deleteValuations({valuations}));
                this.selection.clear();
                this.selectMode = false;
            }
        });
    }

    onSearchInput(event: Event) {
        const value = (event.target as HTMLInputElement).value;
        this.searchSubject.next(value);
    }

    onSortChange(sort: Sort) {
        if (sort.direction) {
            this.sortActive = sort.active;
            this.sortDirection = sort.direction;
        } else {
            this.sortActive = "fundName";
            this.sortDirection = "asc";
        }
        const sortString = `${this.sortActive},${this.sortDirection}`;
        this.store.dispatch(FundValuationOverviewActions.changeSort({sort: sortString}));
    }

    onPageChange(event: PageEvent) {
        this.store.dispatch(FundValuationOverviewActions.changePage({page: event.pageIndex, size: event.pageSize}));
    }

    toggleSearch() {
        this.searchVisible = !this.searchVisible;
        if (this.searchVisible) {
            setTimeout(() => this.searchInput?.nativeElement.focus());
        } else {
            this.searchSubject.next("");
        }
    }

    openTableSettings() {
        this.store.select(selectFundValuationOverviewSearchParams).pipe(take(1)).subscribe(params => {
            const sortParts = params.sort.split(",");
            const currentSort = {field: sortParts[0] || "fundName", direction: sortParts[1] === "desc" ? "desc" : "asc"} as const;

            const dialogData: TableSettingsDialogData = {
                tableKey: TABLE_KEY,
                columnGroups: ALL_COLUMN_GROUPS,
                columnLabels: COLUMN_LABELS,
                currentColumns: this.columns,
                currentPageSize: params.size,
                currentSort,
                pageSizeOptions: [10, 20, 30, 50, 100],
                sortableColumns: SORTABLE_COLUMNS,
                isAdmin: false,
                defaultColumns: FundValuationOverviewPageComponent.ALL_VALID_COLUMNS,
                defaultPageSize: 20,
                defaultSort: {field: "fundName", direction: "asc"},
            };

            this.dialogSubscription = this.dialog.open(TableSettingsDialogComponent, {data: dialogData, width: "520px"})
                .afterClosed()
                .subscribe((result: TableSettingsDialogResult) => this.handleDialogResult(result));
        });
    }

    navigateToFundValuation(entry: FundValuationOverviewItem, dealId?: number): void {
        const queryParams: any = {
            fundReportId: entry.fundReportId,
            fundValuationId: entry.fundValuationId
        };

        if (dealId !== undefined) {
            queryParams.dealId = dealId;
        }

        this.router.navigate(["/funds", entry.fundId], {queryParams});
    }

    ngOnDestroy() {
        this.searchSubscription?.unsubscribe();
        this.settingsSubscription?.unsubscribe();
        this.columnsSubscription?.unsubscribe();
        this.dialogSubscription?.unsubscribe();
    }

    private applySettingsFromStore() {
        combineLatest([
            this.store.select(selectTableColumns(TABLE_KEY)),
            this.store.select(selectTablePageSize(TABLE_KEY)),
            this.store.select(selectTableSort(TABLE_KEY))
        ]).pipe(take(1)).subscribe(([columns, pageSize, sort]) => {
            if (columns) {
                const valid = this.filterValidColumns(columns);
                if (valid) this.columns = valid;
            }
            if (sort) {
                this.sortActive = sort.field;
                this.sortDirection = sort.direction as "asc" | "desc";
            }
            this.store.dispatch(FundValuationOverviewActions.applySettings({
                size: pageSize ?? undefined,
                sort: sort ? `${sort.field},${sort.direction}` : undefined
            }));
            this.store.dispatch(FundValuationOverviewActions.search());
        });
    }

    private filterValidColumns(columns: string[]): string[] | null {
        const valid = columns.filter(c => FundValuationOverviewPageComponent.ALL_VALID_COLUMNS.includes(c));
        return valid.length > 0 ? valid : null;
    }

    private handleDialogResult(result: TableSettingsDialogResult) {
        if (!result) return;

        this.columns = result.columns;
        this.sortActive = result.sort.field;
        this.sortDirection = result.sort.direction as "asc" | "desc";
        this.store.dispatch(FundValuationOverviewActions.applySettings({
            size: result.pageSize,
            sort: `${result.sort.field},${result.sort.direction}`
        }));
        this.store.dispatch(FundValuationOverviewActions.search());

        const payload = {tableKey: TABLE_KEY, columns: result.columns, pageSize: result.pageSize, sort: result.sort};

        if (result.saveAdmin) {
            this.store.dispatch(UserSettingActions.saveAdminTableSettings(payload));
        }
        if (result.saveUser) {
            this.store.dispatch(UserSettingActions.saveTableSettings(payload));
        }
        if (!result.saveUser && !result.saveAdmin) {
            this.store.dispatch(UserSettingActions.applyTableSettings(payload));
        }
    }
}
